import {useMemo} from "react";
import {Badge, Form, InputGroup, Table} from "react-bootstrap";
import {Link, useNavigate} from "react-router";
import {SupportApi} from "../../api/SupportApi";
import {isOpenTicketState, SupportTicketState, SupportTicketStates} from "../../api/models";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {formatDateTime} from "../../components/format";
import {SortHeader, useSort} from "../../components/sortable";
import {usePersistentState} from "../../components/usePersistentState";
import {SupportStateBadge} from "../support/SupportParts";
import {format, support} from "../../i18n";

type StateFilter = 'open' | 'all' | SupportTicketState;
type CategoryFilter = 'all' | 'support' | 'press';

const isString = (value: unknown): value is string => typeof value === 'string';
const isStateFilter = (value: unknown): value is StateFilter =>
    value === 'open' || value === 'all' || (SupportTicketStates as unknown[]).includes(value);
const isCategoryFilter = (value: unknown): value is CategoryFilter => value === 'all' || value === 'support' || value === 'press';

// Every customer's support tickets, and press inquiries (/press/contact), marked Press. By default the open ones (not
// Resolved or Closed), of either kind.
export function SupportTicketsAdminPage() {
    const navigate = useNavigate();
    const {data: tickets, error, loading} = useAsync(() => SupportApi.getAll({ all: true }), []);
    const [filter, setFilter] = usePersistentState('admin-support:filter', '', isString);
    const [stateFilter, setStateFilter] = usePersistentState<StateFilter>('admin-support:state', 'open', isStateFilter);
    const [categoryFilter, setCategoryFilter] = usePersistentState<CategoryFilter>('admin-support:category', 'all', isCategoryFilter);

    const shown = useMemo(() => {
        const text = filter.trim().toLowerCase();
        return (tickets ?? [])
            .filter(x => stateFilter === 'all' || (stateFilter === 'open' ? isOpenTicketState(x.state) : x.state === stateFilter))
            .filter(x => categoryFilter === 'all' || (x.category ?? 'support') === categoryFilter)
            .filter(x => !text || [x.subject, x.ownerUsername, x.customerName ?? '', x.customerEmail ?? '', x.productName, x.press?.publication ?? ''].some(field => field.toLowerCase().includes(text)));
    }, [tickets, filter, stateFilter, categoryFilter]);
    const {sorted, sort, toggle} = useSort('admin-support:sort', shown, {
        subject: x => x.subject,
        customer: x => x.ownerUsername || x.customerName || '',
        product: x => x.productName,
        state: x => SupportTicketStates.indexOf(x.state),
        messages: x => x.messageCount,
        opened: x => x.createdOn,
        activity: x => x.lastActivityOn
    });

    return (
        <>
            <PageHeader title="Support tickets"/>
            <ErrorAlert error={error}/>
            {loading && !tickets && <LoadingSpinner/>}
            {tickets && (
                <>
                    <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
                        <InputGroup style={{ maxWidth: '24rem' }}>
                            <InputGroup.Text><i className="bi bi-search" aria-hidden="true"></i></InputGroup.Text>
                            <Form.Control placeholder="Filter by subject, customer or product" value={filter} onChange={e => setFilter(e.target.value)} aria-label="Filter"/>
                        </InputGroup>
                        <Form.Select style={{ maxWidth: '14rem' }} value={stateFilter} onChange={e => setStateFilter(e.target.value as StateFilter)} aria-label="Status">
                            <option value="open">Open tickets</option>
                            <option value="all">All tickets</option>
                            {SupportTicketStates.map(x => <option key={x} value={x}>{support.states[x]}</option>)}
                        </Form.Select>
                        <Form.Select style={{ maxWidth: '14rem' }} value={categoryFilter} onChange={e => setCategoryFilter(e.target.value as CategoryFilter)} aria-label="Kind">
                            <option value="all">Support and press</option>
                            <option value="support">Support tickets</option>
                            <option value="press">Press inquiries</option>
                        </Form.Select>
                    </div>
                    <Table responsive hover>
                        <thead>
                        <tr>
                            <SortHeader column="subject" sort={sort} onSort={toggle}>Subject</SortHeader>
                            <SortHeader column="customer" sort={sort} onSort={toggle}>Customer</SortHeader>
                            <SortHeader column="product" sort={sort} onSort={toggle}>Product</SortHeader>
                            <SortHeader column="state" sort={sort} onSort={toggle}>Status</SortHeader>
                            <SortHeader column="messages" sort={sort} onSort={toggle}>Messages</SortHeader>
                            <SortHeader column="opened" sort={sort} onSort={toggle}>Opened</SortHeader>
                            <SortHeader column="activity" sort={sort} onSort={toggle}>Last activity</SortHeader>
                        </tr>
                        </thead>
                        <tbody>
                        {sorted.map(ticket => (
                            <tr key={ticket.id} className="clickable-row" onClick={() => navigate(`/admin/support/${ticket.id}`)}>
                                <td>
                                    {ticket.category === 'press' && <Badge bg="info" text="dark" className="me-2">Press</Badge>}
                                    <Link to={`/admin/support/${ticket.id}`} onClick={e => e.stopPropagation()}>{ticket.subject}</Link>
                                    {ticket.press?.reviewLicense && <Badge bg="warning" text="dark" className="ms-2">Review license</Badge>}
                                </td>
                                <td>
                                    {ticket.ownerUsername || ticket.customerName}
                                    {!ticket.ownerUsername && <div className="small text-body-secondary text-break">{format(support.ticket.details.customerEmail, { email: ticket.customerEmail ?? '' })}</div>}
                                    {ticket.press && <div className="small text-body-secondary">{ticket.press.publication}</div>}
                                </td>
                                <td>{ticket.licenseId || ticket.category === 'press' ? ticket.productName || '—' : support.generalTicket}</td>
                                <td><SupportStateBadge state={ticket.state}/></td>
                                <td>{ticket.messageCount}</td>
                                <td className="text-nowrap">{formatDateTime(ticket.createdOn)}</td>
                                <td className="text-nowrap">{formatDateTime(ticket.lastActivityOn)}</td>
                            </tr>
                        ))}
                        {shown.length === 0 && <tr><td colSpan={7} className="text-center text-body-secondary py-4">No tickets{(filter || stateFilter !== 'all' || categoryFilter !== 'all') && ' match the filter'}.</td></tr>}
                        </tbody>
                    </Table>
                </>
            )}
        </>
    );
}
